/**
 * Privacy-first analytics - Plausible-compatible, zero dependencies.
 *
 * Activates only if:
 *   1. VITE_PLAUSIBLE_DOMAIN is set at build time, AND
 *   2. The user has not opted out via localStorage flag, AND
 *   3. The user has not enabled Do Not Track (DNT).
 *
 * Tracks ONLY: page-view, tool-selected, conversion-completed.
 * NEVER tracks: file contents, file names, geometry, attribute values.
 */

const STORAGE_OPT_OUT_KEY = "shpaid.analytics.optout";
const ENDPOINT = "https://plausible.io/api/event";
const DOMAIN = import.meta.env.VITE_PLAUSIBLE_DOMAIN as string | undefined;

function isOptedOut(): boolean {
  try {
    if (localStorage.getItem(STORAGE_OPT_OUT_KEY) === "1") return true;
  } catch { /* localStorage disabled */ }
  if (typeof navigator !== "undefined" && navigator.doNotTrack === "1") return true;
  return false;
}

function isEnabled(): boolean {
  return !!DOMAIN && !isOptedOut();
}

interface TrackProps { [k: string]: string | number | boolean | undefined; }

export function track(name: string, props?: TrackProps): void {
  if (!isEnabled()) return;
  try {
    void fetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      keepalive: true,                       // survive page-unload
      body: JSON.stringify({
        name,
        // Strip the URL fragment: it is never needed for analytics and can hold
        // sensitive data (e.g. the Visualize share decryption key in `#k=...`).
        url: location.href.split("#")[0],
        domain: DOMAIN,
        referrer: document.referrer || null,
        props: props ?? {},
      }),
    });
  } catch { /* fail-silently - analytics should never break the app */ }
}

export function trackPageView(): void {
  track("pageview");
}

export function trackToolSelected(toolId: string): void {
  // Just the tool id (enum), no user data.
  track("tool_selected", { tool: toolId });
}

// ─── User controls ─────────────────────────────────────────────────────────────

export function optOut(): void {
  try { localStorage.setItem(STORAGE_OPT_OUT_KEY, "1"); } catch { /* noop */ }
}
export function optIn(): void {
  try { localStorage.removeItem(STORAGE_OPT_OUT_KEY); } catch { /* noop */ }
}
export function isAnalyticsActive(): boolean { return isEnabled(); }

// ─── Error reporting (STAB-1) ────────────────────────────────────────────────
// Privacy-safe: sends only technical fields (error name, a non-reversible code,
// a coarse route, scope) through the same opt-out/DNT-gated channel. Never sends
// the raw message, URL query, fragment, file names, or geometry.

function coarseRoute(): string {
  // First path segment only, so tokens/ids (e.g. /s/:token) are not sent.
  const seg = location.pathname.split("/").filter(Boolean)[0];
  return seg ? `/${seg}` : "/";
}

/** Deterministic 6-char code for dedup/traceability - no PII. */
export function errorCode(input: string): string {
  let h = 0;
  for (let i = 0; i < input.length; i++) h = ((h << 5) - h + input.charCodeAt(i)) | 0;
  return Math.abs(h).toString(36).slice(0, 6).toUpperCase();
}

const MAX_ERRORS_PER_SESSION = 10;
let errorsSent = 0;
const BENIGN = /ResizeObserver loop|^Script error\.?$/i;

export function trackError(name: string, code: string, scope = "app", chunk = false): void {
  if (errorsSent >= MAX_ERRORS_PER_SESSION) return;
  errorsSent++;
  track("js_error", { name: name.slice(0, 60), code, scope, chunk, route: coarseRoute() });
}

/** Wire global handlers for errors that escape React's boundary. Call once at boot. */
export function initErrorReporting(): void {
  if (typeof window === "undefined") return;
  window.addEventListener("error", (e) => {
    const msg = e.message || e.error?.name || "error";
    if (BENIGN.test(msg)) return;
    trackError(e.error?.name ?? "Error", errorCode(msg), "window");
  });
  window.addEventListener("unhandledrejection", (e) => {
    const r = e.reason;
    const name = (r && (r as Error).name) || "UnhandledRejection";
    const msg = (r && ((r as Error).message || String(r))) || "rejection";
    if (BENIGN.test(msg)) return;
    trackError(name, errorCode(msg), "promise");
  });
}
